/**
 * S-05 Email verification interstitial (V1 auth-profiles-gdpr).
 *
 * Two modes on one route:
 *  - /verify-email?token=… (the emailed link) → POST /auth/verify-email/consume;
 *    success verifies the address (lifecycle → onboarding). If the signup
 *    password is still in memory the screen logs straight in; otherwise it
 *    hands off to sign-in.
 *  - /verify-email (no token) → the "check your inbox" pending screen:
 *    resend (60 s client throttle; authed sessions use /verify-email/resend,
 *    anonymous ones use the magic link — clicking it verifies AND signs in,
 *    #80b), change-address link, and the "Delete my data" footer (pre-ACTIVE
 *    deletes immediately, no grace).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { copy, resolveCopy } from "@humble/shared";
import { Button, Card, IconBrokenHeart, toast } from "../../design/index.js";
import { api, getDeviceFp, isApiError } from "../../lib/api.js";
import { sessionStore, setSession, useStore } from "../../lib/store.js";
import { DeleteDataFooter } from "../account/delete-sheet.js";
import { routeForLifecycle, signupFlow } from "./signup-flow.js";
import "./auth.css";

const RESEND_THROTTLE_MS = 60_000;

type ConsumeState = "consuming" | "verified" | "failed";

export function VerifyEmailScreen() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <ConsumeToken token={token} /> : <PendingInbox />;
}

// ---------------------------------------------------------------------------
// token-consume mode
// ---------------------------------------------------------------------------
function ConsumeToken({ token }: { token: string }) {
  const navigate = useNavigate();
  const [state, setState] = useState<ConsumeState>("consuming");
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-mount must not burn the token twice
    ran.current = true;
    (async () => {
      try {
        await api.post("/auth/verify-email/consume", { body: { token } });
        setState("verified");
        // Same-tab flow: signup credentials may still be in memory — log in.
        const { email, password } = signupFlow.get();
        if (email && password) {
          try {
            const pair = await api.post("/auth/login", {
              body: { email, password, device_fp: getDeviceFp() },
            });
            if (pair?.access_token) {
              setSession(pair.access_token, pair.user ?? null, pair.lifecycle ?? null);
              navigate(routeForLifecycle(pair.lifecycle));
            }
          } catch {
            /* the verified state below offers sign-in */
          }
        }
      } catch (err) {
        setState("failed");
        setError(
          isApiError(err, "TOKEN_INVALID")
            ? (resolveCopy("auth.verify_expired") ?? err.message)
            : isApiError(err)
              ? err.message
              : "Network error — try the link again.",
        );
      }
    })();
  }, [token, navigate]);

  return (
    <div className="auth auth--center" data-screen="S-05">
      <div className="auth__brand">
        <IconBrokenHeart width={24} height={24} style={{ color: "var(--accent)" }} />
        humble
      </div>
      <Card padding="lg">
        {state === "consuming" && <p role="status">Checking that link…</p>}
        {state === "verified" && (
          <>
            <h1 className="auth__title">Email verified</h1>
            <p>Your address is confirmed. Sign in to continue building your profile.</p>
            <Link to="/" className="btn btn--primary btn--block">
              Sign in
            </Link>
          </>
        )}
        {state === "failed" && (
          <>
            <h1 className="auth__title">That link didn't work</h1>
            <p className="field__error" role="alert">
              {error}
            </p>
            <p className="text-dim">
              Already verified? Just sign in. Otherwise request a fresh link below.
            </p>
            <Link to="/" className="btn btn--primary btn--block" style={{ marginBottom: 8 }}>
              Sign in
            </Link>
            <Link to="/verify-email" className="btn btn--ghost btn--block">
              Request a new link
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// pending-inbox mode
// ---------------------------------------------------------------------------
function PendingInbox() {
  const location = useLocation();
  const status = useStore(sessionStore, (s) => s.status);
  const email = useStore(signupFlow, (s) => s.email);
  const existing = Boolean((location.state as { existing?: boolean } | null)?.existing);
  const [lastSent, setLastSent] = useState(0);
  const [sending, setSending] = useState(false);

  async function onResend() {
    const now = Date.now();
    if (sending || now - lastSent < RESEND_THROTTLE_MS) {
      toast("Give the last one a minute to land.", "announcer");
      return;
    }
    setSending(true);
    try {
      if (status === "authed") {
        await api.post("/auth/verify-email/resend");
      } else if (email) {
        // No session pre-verification — the magic link both signs in and
        // verifies (#80b), so it doubles as the resend.
        await api.post("/auth/magic-link", { body: { email } });
      } else {
        toast("Start again from signup — we don't know which inbox to hit.", "error");
        return;
      }
      setLastSent(now);
      toast("Sent. Inbox, spam folder, the usual suspects.", "success");
    } catch (err) {
      if (isApiError(err) && err.status === 429) {
        toast(resolveCopy("auth.throttled") ?? err.message, "error");
      } else {
        setLastSent(now); // 202 semantics — never disclose membership
        toast("Sent. Inbox, spam folder, the usual suspects.", "success");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="auth auth--center" data-screen="S-05">
      <div className="auth__brand">
        <IconBrokenHeart width={24} height={24} style={{ color: "var(--accent)" }} />
        humble
      </div>
      <Card padding="lg">
        <h1 className="auth__title">Check your inbox</h1>
        <p role="status">{copy.auth.verify_wait}</p>
        {existing && (
          <p className="text-dim">
            If that address already had an account, the mail is a sign-in link instead — one
            account per email, all doors lead to it.
          </p>
        )}
        {email && (
          <p className="text-dim">
            Sent to <strong>{email}</strong>.
          </p>
        )}
        <Button block variant="primary" loading={sending} onClick={() => void onResend()}>
          Resend the email
        </Button>
        <div className="auth__row" style={{ marginTop: 8 }}>
          <Link to="/signup/account" className="btn btn--ghost btn--block">
            Change address
          </Link>
          <Link to="/" className="btn btn--ghost btn--block">
            Back to sign in
          </Link>
        </div>
      </Card>
      <div className="auth__footer">
        <DeleteDataFooter />
        <p className="text-faint" style={{ fontSize: "0.8rem", width: "100%" }}>
          Unverified accounts are deleted automatically after 7 days.
        </p>
      </div>
    </div>
  );
}
